
import React, { useState, useEffect } from "react";
import { DataToolbar, ActionButtons } from "../components/DataToolbar";
import { Modal } from "../components/Modal";
import { FileUploadKit } from "../components/common/FileUploadKit";
import { exportToExcel } from "../export/excel";
import { useToast } from "../components/Toast";
import { useAiContext } from "../ai/AiContext";
import { generateDocCode } from "../utils/idGenerator";
import { DateField } from "../components/form/DateField";
import { formatDate, parseDate } from "../utils/dateUtils";

import { db, serverTimestamp } from "../../firebaseClient";
import { uploadCaseFiles } from "../services/storageUpload";
import { createLegalActivity } from "../services/legalActivityService";
import { useAuth } from "../context/AuthContext";
import { SignRequestButton } from "../modules/sign/components/SignRequestButton";
import { canEditModule } from "../config/roles";
import { canViewData } from "../utils/dataAccess";

import type { LegalMatter, MatterType, LegalMatterStatus } from "../types/legalMatter";
import type { AttachmentMeta } from "../types/attachments";
import { logLegalActivityOnChange } from "../services/legalActivityLogger";
import { completeTaskBySource } from "../services/taskService";

// Extended Type for UI
interface ExtendedLegalMatter extends LegalMatter {
  stage?: string;
  disputeAmount?: number;
  winProbability?: 'low' | 'medium' | 'high';
  keyDates?: { label: string; date: string }[];
  lastUpdatedBy?: string;
  riskNotes?: string;
  analysisNotes?: string;
  decisionNotes?: string;
  notes?: string;
  relatedContractNo?: string; 
  noteHistory?: { user: string; date: string; text: string; }[];
}

const STATUS_OPTIONS: LegalMatterStatus[] = ["評估中", "委任準備中", "進行中", "待對方回應", "和解成立", "已結案", "中止 / 撤回"];
const STAGE_OPTIONS = ["詢問中 (Inquiry)", "協商中 (Negotiation)", "已發函 (Notice Sent)", "訴訟中 (Litigation)", "仲裁中 (Arbitration)", "結案 (Closed)"];
const WIN_PROB_OPTIONS = ["low", "medium", "high"];
const IMPORTANCE_OPTIONS = ["High", "Medium", "Low"];

// Helper to construct AppUser object from Firebase Auth user context
function getCurrentAppUser(user: any) {
    if (!user) return null;
    return {
        id: user.id,
        email: user.email || "",
        name: user.displayName || "",
        role: user.role,
        displayName: user.displayName || ""
    };
}

export function LegalMattersPage() {
  const { user } = useAuth();
  const canEdit = user ? canEditModule(user.role, 'CASES') : false;

  const [items, setItems] = useState<ExtendedLegalMatter[]>([]);
  const [filteredItems, setFilteredItems] = useState<ExtendedLegalMatter[]>([]);
  const [error, setError] = useState<Error | null>(null);
  
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState<"ALL" | MatterType>("ALL");
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ExtendedLegalMatter | null>(null);
  const [previewItem, setPreviewItem] = useState<ExtendedLegalMatter | null>(null);

  const [isImportOpen, setIsImportOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [isImporting, setIsImporting] = useState(false);

  const { requestDelete, setActiveItem } = useAiContext();
  const { showToast } = useToast();

  useEffect(() => {
    if (!user) return;
    const unsubscribe = db.collection("legal_matters").orderBy("createdAt", "desc").onSnapshot(
      (snapshot) => {
        const rawList = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as ExtendedLegalMatter));
        const currentUser = getCurrentAppUser(user);
        
        // Apply Security Filter
        const visibleList = rawList.filter(item => canViewData(currentUser, item));
        
        setItems(visibleList);
        setError(null);
      }, 
      (err) => {
        console.error("Legal Matters listener error:", err);
        setError(err);
      }
    );
    return () => unsubscribe();
  }, [user]);

  useEffect(() => {
    let res = items;
    if (search) {
      const s = search.toLowerCase();
      res = res.filter(i => i.title.toLowerCase().includes(s) || i.matterNo.toLowerCase().includes(s));
    }
    if (filterType !== "ALL") res = res.filter(i => i.matterType === filterType);
    setFilteredItems(res);
  }, [items, search, filterType]);

  const handleDelete = (id: string, title: string) => {
    requestDelete({
      targetType: 'legalMatter',
      targetId: id,
      summary: title,
      onConfirm: async () => {
        try {
            await db.collection("legal_matters").doc(id).delete();
            await completeTaskBySource(id); // Clean up tasks
            await logLegalActivityOnChange({
                module: "案件/爭議",
                docId: id,
                title: title,
                action: "刪除",
                userEmail: user?.email || "unknown"
            });
            showToast("已刪除資料");
        } catch(e) { console.error(e); showToast("刪除失敗", "error"); }
      }
    });
  };

  const handleExport = () => {
    exportToExcel("LegalMatters", filteredItems);
  };

  const handleImportSubmit = async () => {
    if (!importFile) return;
    setIsImporting(true);
    try {
        const text = await importFile.text();
        const lines = text.split(/\r?\n/);
        const batch = db.batch();
        let count = 0;
        for (let i = 1; i < lines.length; i++) {
            const line = lines[i].trim();
            if(!line) continue;
            const cols = line.split(",");
            if(cols.length < 2) continue;
            const docRef = db.collection("legal_matters").doc();
            
            const type = (cols[2] === '爭議' ? '爭議' : '案件');
            const matterNo = cols[0] || generateDocCode(type === '爭議' ? 'dispute' : 'case', undefined, items);

            batch.set(docRef, {
                matterNo: matterNo,
                title: cols[1],
                matterType: type,
                status: cols[3] || '進行中',
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            });
            count++;
        }
        await batch.commit();
        showToast(`匯入成功 ${count} 筆`);
        setIsImportOpen(false);
        setImportFile(null);
    } catch(e) {
        console.error(e);
        showToast("匯入失敗", "error");
    } finally {
        setIsImporting(false);
    }
  };

  const handleOpenCreate = () => {
      setEditingItem(null); 
      setIsModalOpen(true);
  };

  const handleEdit = (item: ExtendedLegalMatter) => {
      setEditingItem(item);
      setIsModalOpen(true);
  };

  return (
    <div>
      <div className="section" style={{ marginBottom: 16, padding: "12px 16px" }}>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <input className="input" style={{ width: 250 }} placeholder="搜尋編號、標題..." value={search} onChange={e => setSearch(e.target.value)} />
          <select className="input" style={{ width: 120 }} value={filterType} onChange={e => setFilterType(e.target.value as any)}>
             <option value="ALL">所有類型</option>
             <option value="案件">案件</option>
             <option value="爭議">爭議</option>
          </select>
        </div>
      </div>

      <DataToolbar 
        canEdit={canEdit} 
        onCreate={handleOpenCreate} 
        onExport={handleExport}
        onImport={() => setIsImportOpen(true)}
      />

      {error && <div className="err" style={{marginBottom:16}}>讀取失敗: {error.message}</div>}

      <div style={{ display: "grid", gap: 10 }}>
        {filteredItems.map(item => (
          <div key={item.id} className="tile" data-legal-matter-id={item.id}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
               <div style={{ fontSize: 13, color: "var(--muted)" }}>
                  <span className="badge" style={{ marginRight: 8, background: item.matterType === "案件" ? "#2563eb" : "#d97706", color: "#fff", border: "none" }}>{item.matterType}</span>
                  {item.matterNo}
                  {item.importance === 'High' && <span className="badge" style={{marginLeft: 8, background: "var(--danger)", color: "#fff", border: "none"}}>重要</span>}
               </div>
               <div className="badge" style={{ color: item.status === "已結案" ? "var(--muted)" : "var(--ok)", borderColor: "currentColor" }}>{item.status}</div>
            </div>
            <div style={{ fontSize: 16, fontWeight: "bold", color: "#fff", marginBottom: 4 }}>{item.title}</div>
            <div className="grid" style={{ gap: 8, gridTemplateColumns: "1fr auto" }}>
               <div style={{ fontSize: 13, color: "var(--muted)" }}>
                  <div style={{ marginBottom: 4 }}>對造: <span style={{color:"var(--text)"}}>{item.counterparty || "-"}</span></div>
                  <div>階段: {item.stage || "-"} | 金額: {item.disputeAmount ? `$${item.disputeAmount.toLocaleString()}` : "-"}</div>
                  <div>負責: {item.department} {item.owner ? `(${item.owner})` : ""}</div>
                  {item.relatedContractNo && <div style={{marginTop:4, color:'#a5b4fc'}}>關聯合約: {item.relatedContractNo}</div>}
               </div>
               <div style={{ alignSelf: "flex-end", textAlign: "right" }}>
                  <div style={{fontSize: 12, color: item.winProbability === 'low' ? 'var(--danger)' : item.winProbability === 'high' ? 'var(--ok)' : 'var(--muted)', marginBottom: 4}}>
                     {item.winProbability ? `勝率: ${item.winProbability}` : ""}
                  </div>
                  <div style={{display:'flex', gap:8, justifyContent:'flex-end', marginBottom: 8}}>
                     <SignRequestButton 
                        sourceType="case" 
                        sourceId={item.id} 
                        sourceTitle={item.title} 
                        defaultCategory="案件處理" 
                     />
                  </div>
                  <ActionButtons canEdit={canEdit} onPreview={() => setPreviewItem(item)} onEdit={() => handleEdit(item)} onDelete={() => handleDelete(item.id, item.title)} />
               </div>
            </div>
          </div>
        ))}
        {filteredItems.length === 0 && <div className="p" style={{textAlign:"center", padding:20}}>無符合資料</div>}
      </div>

      {previewItem && <LegalMatterPreviewModal data={previewItem} onClose={() => setPreviewItem(null)} />}
      
      {isModalOpen && (
        <LegalMatterFormModal 
          initialData={editingItem} 
          existingItems={items}
          onClose={() => setIsModalOpen(false)} 
          onSuccess={() => { showToast(editingItem ? "已更新" : "已新增"); setIsModalOpen(false); }} 
        />
      )}

      {isImportOpen && (
        <Modal title="匯入案件/爭議" onClose={() => setIsImportOpen(false)} width={500}>
           <div className="p">請上傳 CSV 檔案 (格式: 編號,標題,類型,狀態)</div>
           <input type="file" accept=".csv" onChange={e => setImportFile(e.target.files?.[0] || null)} style={{margin: '20px 0'}} />
           <div className="row" style={{justifyContent: 'flex-end'}}>
              <button className="btn" onClick={handleImportSubmit} disabled={!importFile || isImporting}>{isImporting?"匯入中...":"匯入"}</button>
           </div>
        </Modal>
      )}
    </div>
  );
}

function LegalMatterFormModal({ initialData, existingItems, onClose, onSuccess }: { initialData: ExtendedLegalMatter | null, existingItems: ExtendedLegalMatter[], onClose: () => void, onSuccess: () => void }) {
  const [form, setForm] = useState<Partial<ExtendedLegalMatter>>(initialData || { 
    matterType: "案件", 
    status: "進行中", 
    stage: "Inquiry", 
    importance: "Medium",
    attachments: [], 
    keyDates: [] 
  });
  const [newDateLabel, setNewDateLabel] = useState("");
  const [newDateVal, setNewDateVal] = useState("");
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { setDraft } = useAiContext();
  const { user } = useAuth();

  const handleChange = (k: keyof ExtendedLegalMatter, v: any) => setForm(p => ({ ...p, [k]: v }));

  // Auto Generate ID on init if new
  useEffect(() => {
      if (!initialData && !form.matterNo) {
          const newId = generateDocCode(form.matterType === "爭議" ? "dispute" : "case", undefined, existingItems);
          handleChange("matterNo", newId);
      }
  }, [initialData, form.matterType]);

  useEffect(() => {
    setDraft({ 
        type: 'case', 
        data: form,
        onApplyResult: (target, content) => {
            if (target === "summary") handleChange("summary", content);
            else if (target === "risk") handleChange("riskNotes", content);
            else if (target === "analysis") handleChange("analysisNotes", content);
            else if (target === "decision") handleChange("decisionNotes", content);
            else if (target === "note") handleChange("notes", (form.notes||"") + "\n" + content);
        }
    });
    return () => setDraft(null);
  }, [form, setDraft]);

  const handleAddKeyDate = () => {
      if(newDateLabel && newDateVal) {
          handleChange("keyDates", [...(form.keyDates||[]), { label: newDateLabel, date: newDateVal }]);
          setNewDateLabel(""); setNewDateVal("");
      }
  };

  const handleRegenerateNo = () => {
      const newId = generateDocCode(form.matterType === "爭議" ? "dispute" : "case", undefined, existingItems);
      handleChange("matterNo", newId);
  };

  const handleSave = async () => {
    if (!form.title) { alert("請填寫標題"); return; }
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
        const userName = user?.displayName || "User";
        const now = new Date().toISOString();
        const payload = { ...form, lastUpdatedBy: userName, lastUpdatedAt: serverTimestamp() };
        if (!payload.attachments) payload.attachments = [];
        const existingAttachments = payload.attachments as AttachmentMeta[];

        // Handle Note History - Always log change
        if (form.notes !== initialData?.notes && form.notes) {
            payload.noteHistory = [...(form.noteHistory || []), {
                user: userName,
                date: now.slice(0, 16).replace("T", " "),
                text: form.notes
            }];
        }

        let docId = payload.id;
        const action = docId ? "修改" : "新增";
        
        if (initialData && docId) {
            delete payload.id;
            await db.collection("legal_matters").doc(docId).update({ ...payload, updatedAt: serverTimestamp() });
        } else {
            delete payload.id;
            if (!payload.matterNo) {
                payload.matterNo = generateDocCode(payload.matterType === "爭議" ? "dispute" : "case", undefined, existingItems);
            }
            const ref = await db.collection("legal_matters").add({ ...payload, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
            docId = ref.id;
        }

        if (pendingFiles.length > 0 && docId) {
            const newAttachments = await uploadCaseFiles(docId, pendingFiles);
            const final = [...existingAttachments, ...newAttachments];
            await db.collection("legal_matters").doc(docId).update({ attachments: final });
        }

        // Auto-Complete Task if closed
        if (docId && (payload.status === "已結案" || payload.status === "和解成立" || payload.status === "中止 / 撤回")) {
            await completeTaskBySource(docId);
        }

        // Trigger log activity (which also Creates task if new)
        await logLegalActivityOnChange({
            module: payload.matterType || "案件",
            docId: docId || "unknown",
            title: payload.title || "",
            action: action,
            userEmail: user?.email || "unknown"
        });

        onSuccess();
    } catch(e) {
        console.error(e);
        alert("儲存失敗");
    } finally {
        setIsSubmitting(false);
    }
  };

  return (
    <Modal title={initialData ? "編輯資料" : "新增案件／爭議"} onClose={onClose} width={800}>
      <div className="grid">
        <div className="field"><label className="label">類型</label><select className="input" value={form.matterType} onChange={e=>handleChange("matterType", e.target.value)}><option value="案件">案件</option><option value="爭議">爭議</option></select></div>
        <div className="field"><label className="label">編號 (系統自動)</label>
            <div style={{display:"flex", gap:8}}>
                <input className="input" value={form.matterNo||""} readOnly style={{background: 'rgba(255,255,255,0.05)', color: '#fbbf24', fontWeight:'bold', border:'1px dashed #555'}} />
                {!initialData && (
                    <button className="smallbtn" style={{width:"auto"}} onClick={handleRegenerateNo} title="重新產生編號">↻</button>
                )}
            </div>
        </div>
        <div className="field"><label className="label">標題 (必填)</label><input className="input" value={form.title||""} onChange={e=>handleChange("title", e.target.value)} /></div>
        
        <div className="field"><label className="label">關聯合約 (CT-xxx)</label><input className="input" value={form.relatedContractNo||""} onChange={e=>handleChange("relatedContractNo", e.target.value)} placeholder="CT-202512..." /></div>

        <div className="field"><label className="label">階段</label><select className="input" value={form.stage} onChange={e=>handleChange("stage", e.target.value)}><option value="">請選擇</option>{STAGE_OPTIONS.map(o=><option key={o} value={o}>{o}</option>)}</select></div>
        <div className="field"><label className="label">對造／相關單位</label><input className="input" value={form.counterparty||""} onChange={e=>handleChange("counterparty", e.target.value)} /></div>
        <div className="field"><label className="label">負責部門</label><input className="input" value={form.department||""} onChange={e=>handleChange("department", e.target.value)} /></div>
        <div className="field"><label className="label">負責人</label><input className="input" value={form.owner||""} onChange={e=>handleChange("owner", e.target.value)} /></div>
        <div className="field"><label className="label">涉案金額</label><input type="number" className="input" value={form.disputeAmount||""} onChange={e=>handleChange("disputeAmount", Number(e.target.value))} /></div>
        <div className="field"><label className="label">勝率評估</label><select className="input" value={form.winProbability} onChange={e=>handleChange("winProbability", e.target.value)}>{WIN_PROB_OPTIONS.map(o=><option key={o} value={o}>{o}</option>)}</select></div>
        <div className="field"><label className="label">重要性</label><select className="input" value={form.importance || "Medium"} onChange={e=>handleChange("importance", e.target.value as any)}>{IMPORTANCE_OPTIONS.map(o=><option key={o} value={o}>{o}</option>)}</select></div>
        <div className="field"><label className="label">狀態</label><select className="input" value={form.status} onChange={e=>handleChange("status", e.target.value)}>{STATUS_OPTIONS.map(o=><option key={o} value={o}>{o}</option>)}</select></div>
        
        <div className="field" style={{gridColumn:"1/-1"}}>
           <label className="label">摘要說明 (事件經過)</label>
           <textarea className="input" rows={3} value={form.summary||""} onChange={e=>handleChange("summary", e.target.value)} placeholder="請輸入案件摘要..." />
        </div>

        <div className="field" style={{gridColumn:"1/-1"}}>
           <label className="label" style={{color:"#8b5cf6"}}>法務分析 (AI 支援)</label>
           <textarea className="input" rows={3} value={form.analysisNotes||""} onChange={e=>handleChange("analysisNotes", e.target.value)} placeholder="請輸入法務分析..." />
        </div>
        <div className="field" style={{gridColumn:"1/-1"}}>
           <label className="label" style={{color:"#fbbf24"}}>風險評估 (AI 支援)</label>
           <textarea className="input" rows={3} value={form.riskNotes||""} onChange={e=>handleChange("riskNotes", e.target.value)} placeholder="請輸入風險評估..." />
        </div>

        <div className="field" style={{gridColumn:"1/-1"}}>
           <label className="label">重要日期 (同步至行事曆)</label>
           <div style={{display:"flex", gap:8, marginBottom:8, alignItems: "flex-end"}}>
              <div style={{flex:1}}>
                <input className="input" placeholder="事件名稱 (如: 第一次開庭)" value={newDateLabel} onChange={e=>setNewDateLabel(e.target.value)} />
              </div>
              <div>
                <DateField value={parseDate(newDateVal)} onChange={d=>setNewDateVal(formatDate(d))} />
              </div>
              <button className="smallbtn" style={{width:"auto", height: 40}} onClick={handleAddKeyDate}>新增</button>
           </div>
           <div style={{display:'flex', flexWrap:'wrap', gap:4}}>
             {form.keyDates?.map((k,i)=><div key={i} className="badge" style={{marginRight:4}}>{k.date} {k.label}</div>)}
           </div>
        </div>
      </div>
      
      <FileUploadKit 
        existingFiles={form.attachments || []} 
        onFilesSelected={(fs) => setPendingFiles(prev => [...prev, ...fs])}
        onRemoveFile={(f, index) => {
            const next = [...(form.attachments || [])];
            next.splice(index, 1);
            handleChange("attachments", next);
        }}
      />
      
      <div className="row" style={{ marginTop: 20, justifyContent: "flex-end" }}><button className="btn" style={{ width: "auto" }} disabled={isSubmitting} onClick={handleSave}>儲存 / 建立</button></div>
    </Modal>
  );
}

function LegalMatterPreviewModal({ data, onClose }: { data: ExtendedLegalMatter, onClose: () => void }) {
  return (
    <Modal title={`預覽: ${data.matterNo}`} onClose={onClose} width={700}>
       <div className="grid">
          <div className="p" style={{gridColumn: "1/-1", fontSize: 16, fontWeight: "bold", color: "#fff"}}>{data.title}</div>
          <div className="p">對造: {data.counterparty}</div>
          <div className="p">負責: {data.department} {data.owner ? `(${data.owner})` : ""}</div>
          <div className="p">狀態: {data.status} / {data.stage}</div>
          <div className="p">金額: {data.disputeAmount}</div>
          {data.relatedContractNo && <div className="p">關聯合約: {data.relatedContractNo}</div>}
          <div className="p">重要性: {data.importance || "Medium"}</div>
          <div className="p" style={{gridColumn:"1/-1", background:"rgba(255,255,255,0.05)", padding:8, borderRadius:4}}>摘要: {data.summary || "(無)"}</div>
          {data.analysisNotes && <div className="p" style={{gridColumn:"1/-1"}}>分析: {data.analysisNotes}</div>}
          {data.riskNotes && <div className="p" style={{gridColumn:"1/-1"}}>風險: {data.riskNotes}</div>}
          <div className="p" style={{gridColumn:"1/-1"}}>重要日期: {data.keyDates?.map(k => `${k.date} ${k.label}`).join(", ")}</div>
       </div>
       <div style={{marginTop:20}}><FileUploadKit existingFiles={data.attachments||[]} disabled /></div>
    </Modal>
  );
}
